import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ShowStatus, StatusMap } from '../types/show';

const STORAGE_KEY = 'nyt-best-tv-checklist-v1';

function loadStatuses(): StatusMap {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StatusMap) : {};
  } catch {
    return {};
  }
}

export function useWatchlist() {
  const [statuses, setStatuses] = useState<StatusMap>(loadStatuses);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(statuses));
    } catch {
      // Storage unavailable — progress simply won't persist.
    }
  }, [statuses]);

  const toggle = useCallback((rank: number, status: ShowStatus) => {
    setStatuses((prev) => {
      const next = { ...prev };
      if (next[rank] === status) {
        delete next[rank];
      } else {
        next[rank] = status;
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => setStatuses({}), []);

  const { seenCount, wantCount } = useMemo(() => {
    const values = Object.values(statuses);
    return {
      seenCount: values.filter((s) => s === 'seen').length,
      wantCount: values.filter((s) => s === 'want').length,
    };
  }, [statuses]);

  return { statuses, toggle, reset, seenCount, wantCount };
}
